import React, { useId, useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import {
    Area,
    AreaChart,
    CartesianGrid,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { colors } from "../../theme/colors";
import ClientResponsiveContainer from "./ClientResponsiveContainer";
import { TooltipShell } from "../common/Tooltip";
import ProfileMatchTooltipHeader, {
    ProfileMatchTooltipDivider,
    TOOLTIP_ROW_SX,
} from "./ProfileMatchTooltipHeader";
import {
    buildEloProgressionSeries,
    buildGameAxisTicks,
} from "./profileRecentMatches";
import { ProfileChartSkeleton } from "./profileSkeletons";

function formatEloAxisTick(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return "";
    if (Math.abs(n) >= 1000) {
        return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1).replace(/\.0$/, "")}K`;
    }
    return String(Math.round(n));
}

const AXIS_TICK_TYPOGRAPHY_SX = {
    fontSize: "0.68rem",
    fontWeight: 500,
    color: colors.text.muted,
    lineHeight: 1,
    fontVariantNumeric: "tabular-nums",
};

function ChartYAxisTick({ x, y, payload }) {
    return (
        <g transform={`translate(${x},${y})`}>
            <foreignObject x={-36} y={-8} width={32} height={18}>
                <Typography component="div" sx={{ ...AXIS_TICK_TYPOGRAPHY_SX, textAlign: "right" }}>
                    {formatEloAxisTick(payload.value)}
                </Typography>
            </foreignObject>
        </g>
    );
}

function ChartXAxisTick({ x, y, payload }) {
    return (
        <g transform={`translate(${x},${y})`}>
            <foreignObject x={-24} y={4} width={48} height={18}>
                <Typography component="div" sx={{ ...AXIS_TICK_TYPOGRAPHY_SX, textAlign: "center" }}>
                    {payload.value}
                </Typography>
            </foreignObject>
        </g>
    );
}

function TooltipInfoRow({ label, children, showDivider = true }) {
    return (
        <>
            {showDivider ? <ProfileMatchTooltipDivider /> : null}
            <Box
                sx={{
                    ...TOOLTIP_ROW_SX,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "1rem",
                }}
            >
                <Typography
                    sx={{
                        fontSize: "0.78rem",
                        fontWeight: 600,
                        color: colors.text.secondary,
                        lineHeight: 1,
                    }}
                >
                    {label}
                </Typography>
                {children}
            </Box>
        </>
    );
}

TooltipInfoRow.propTypes = {
    label: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    showDivider: PropTypes.bool,
};

function EloProgressionTooltip({ active, payload }) {
    if (!active || !payload?.length) {
        return null;
    }

    const row = payload[0]?.payload;
    if (!row) {
        return null;
    }

    const eloChange = Number(row.eloChange);
    const deltaUp = Number.isFinite(eloChange) && eloChange > 0;
    const deltaDown = Number.isFinite(eloChange) && eloChange < 0;
    const deltaColor = deltaUp
        ? colors.state.success.main
        : deltaDown
            ? colors.state.danger.main
            : colors.text.muted;

    return (
        <TooltipShell sx={{ minWidth: "12rem", padding: 0, overflow: "hidden" }}>
            <ProfileMatchTooltipHeader
                timestamp={row.timestamp}
                won={row.won}
                myScore={row.myScore}
                oppScore={row.oppScore}
            />

            <TooltipInfoRow label="K/D/A" showDivider={false}>
                <Typography
                    sx={{
                        fontSize: "0.82rem",
                        fontWeight: 700,
                        color: colors.text.primary,
                        fontVariantNumeric: "tabular-nums",
                        lineHeight: 1,
                    }}
                >
                    {row.kills} / {row.deaths} / {row.assists}
                </Typography>
            </TooltipInfoRow>

            <TooltipInfoRow label="ELO">
                <Typography
                    sx={{
                        fontSize: "0.82rem",
                        fontWeight: 700,
                        color: colors.text.primary,
                        fontVariantNumeric: "tabular-nums",
                        lineHeight: 1,
                    }}
                >
                    {Number.isFinite(row.elo) ? row.elo.toLocaleString("en-US") : "—"}
                </Typography>
            </TooltipInfoRow>

            <TooltipInfoRow label="ELO change">
                <Box sx={{ display: "flex", alignItems: "center", gap: "0.2rem" }}>
                    {deltaUp ? (
                        <ArrowUpward sx={{ fontSize: "0.95rem", color: deltaColor }} />
                    ) : null}
                    {deltaDown ? (
                        <ArrowDownward sx={{ fontSize: "0.95rem", color: deltaColor }} />
                    ) : null}
                    <Typography
                        sx={{
                            fontSize: "0.82rem",
                            fontWeight: 700,
                            color: deltaColor,
                            fontVariantNumeric: "tabular-nums",
                            lineHeight: 1,
                        }}
                    >
                        {Number.isFinite(eloChange) ? Math.abs(eloChange) : "—"}
                    </Typography>
                </Box>
            </TooltipInfoRow>
        </TooltipShell>
    );
}

export default function OverviewEloProgressionChart({
    last25MatchData = {},
    isLoading = false,
    maxMatches = 30,
}) {
    const gradId = useId().replace(/:/g, "");
    const { points } = useMemo(
        () => buildEloProgressionSeries(last25MatchData, maxMatches),
        [last25MatchData, maxMatches]
    );

    const yDomain = useMemo(() => {
        if (points.length === 0) {
            return [0, 1];
        }
        const values = points.map((point) => point.elo);
        const min = Math.min(...values);
        const max = Math.max(...values);
        const pad = min === max ? 40 : Math.max(20, Math.round((max - min) * 0.12));
        return [min - pad, max + pad];
    }, [points]);

    const gameAxisTicks = useMemo(
        () => buildGameAxisTicks(points.length),
        [points.length],
    );

    if (isLoading) {
        return <ProfileChartSkeleton height="10rem" />;
    }

    if (points.length < 2) {
        return null;
    }

    return (
        <Box sx={{ width: "100%", height: "10rem", minWidth: 0 }}>
            <ClientResponsiveContainer>
                <AreaChart
                    data={points}
                    margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
                >
                    <defs>
                        <linearGradient id={`${gradId}-fill`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={colors.primary.main} stopOpacity={0.28} />
                            <stop offset="100%" stopColor={colors.primary.main} stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid
                        vertical={false}
                        stroke={alpha(colors.text.muted, 0.08)}
                        strokeDasharray=""
                    />
                    <XAxis
                        dataKey="gameNumber"
                        type="number"
                        domain={[1, Math.max(points.length, 1)]}
                        ticks={gameAxisTicks}
                        tick={<ChartXAxisTick />}
                        axisLine={false}
                        tickLine={false}
                        allowDecimals={false}
                    />
                    <YAxis
                        domain={yDomain}
                        tick={<ChartYAxisTick />}
                        axisLine={false}
                        tickLine={false}
                        width={42}
                    />
                    <Tooltip
                        content={<EloProgressionTooltip />}
                        cursor={{
                            stroke: alpha(colors.primary.main, 0.2),
                            strokeWidth: 1,
                            strokeDasharray: "4 4",
                        }}
                    />
                    <Area
                        type="linear"
                        dataKey="elo"
                        stroke={colors.primary.main}
                        strokeWidth={1.5}
                        fill={`url(#${gradId}-fill)`}
                        dot={false}
                        activeDot={{
                            r: 5,
                            fill: colors.primary.main,
                            stroke: alpha(colors.primary.main, 0.4),
                            strokeWidth: 2,
                        }}
                        isAnimationActive={false}
                    />
                </AreaChart>
            </ClientResponsiveContainer>
        </Box>
    );
}

OverviewEloProgressionChart.propTypes = {
    last25MatchData: PropTypes.object,
    isLoading: PropTypes.bool,
    maxMatches: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
};

function formatProgressionSubtitle(count) {
    if (count <= 0) return "No matches yet";
    if (count === 1) return "Last 1 match";
    return `Last ${count} matches`;
}

export { formatProgressionSubtitle };
